import { RefObject, useEffect } from 'react';

type UseKeyEffectOptions = {
  /** The action to perform when the key is pressed. Should be memoized (e.g useCallback) for performance purposes */
  action: (event: KeyboardEvent) => any;
  /** If we should initialize the listener */
  addEventListener: boolean;
  /** The keyboard (see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key) event key that should trigger the provided `action`. */
  eventKeys: KeyboardEvent['key'][];
  /** The element to which the event listener should be added */
  target: RefObject<EventTarget | null> | EventTarget | null;
};

export const useKeyEffect = ({
  action,
  addEventListener,
  eventKeys,
  target
}: UseKeyEffectOptions) => {
  useEffect(() => {
    const element = target && 'current' in target ? target.current : target;

    const keyboardEvent = (event: Event) => {
      if (eventKeys.includes((event as KeyboardEvent).key)) {
        action(event as KeyboardEvent);
      }
    };

    if (addEventListener && element) {
      element.addEventListener('keydown', keyboardEvent);
    }

    return () => {
      element?.removeEventListener('keydown', keyboardEvent);
    };
  }, [action, addEventListener, eventKeys, target]);
};
